import { update } from 'idb-keyval/dist/compat.js';
import { indexedDBJSONStorage } from './indexedDBStorage';
import { DEFAULT_CACHE_KEY } from '../services/queryParams';

// Before per-ticket partitioning, every ticket's task progress lived in this one
// IndexedDB record, and each tab rewrote the whole thing on every change — so tabs
// working on different tickets overwrote each other's unsynced entries.
export const LEGACY_TASKS_STORAGE_NAME = 'sow-tasks-storage';

// Each ticket persists to `${prefix}${issueKey}-${token}`.
export const TICKET_TASKS_STORAGE_PREFIX = 'sow-tasks-storage-';

// Pulls just this ticket's tasks out of the legacy shared record so work saved
// before the upgrade isn't lost. Returns null when there is nothing to migrate.
const readFromLegacyRecord = async (sowKey) => {
  const legacy = await indexedDBJSONStorage.getItem(LEGACY_TASKS_STORAGE_NAME);
  const legacyTasks = legacy?.state?.jobsTasks?.[sowKey];
  if (!legacyTasks) return null;
  return { ...legacy, state: { ...legacy.state, jobsTasks: { [sowKey]: legacyTasks } } };
};

const ticketTasksOf = (record, sowKey) => record?.state?.jobsTasks?.[sowKey] ?? [];

const modifiedAt = (task) => task.lastModified || 0;

const isFullySubmitted = (tasks) => tasks.length > 0 && tasks.every((task) => task.submitted);

// Per task, the more recently edited copy wins and ties keep the ticket's own record.
// A task only the legacy side has is adopted when it was actually edited there.
const mergeTasksById = (ownTasks, otherTasks, { otherWinsTies = false } = {}) => {
  const ownIds = new Set(ownTasks.map((task) => task.id));
  const otherById = new Map(otherTasks.map((task) => [task.id, task]));
  const merged = ownTasks.map((ownTask) => {
    const otherTask = otherById.get(ownTask.id);
    if (!otherTask) return ownTask;
    const otherIsNewer = modifiedAt(otherTask) > modifiedAt(ownTask);
    const isTieWonByOther = otherWinsTies && modifiedAt(otherTask) === modifiedAt(ownTask);
    return otherIsNewer || isTieWonByOther ? otherTask : ownTask;
  });
  const otherOnly = otherTasks.filter((task) => !ownIds.has(task.id) && modifiedAt(task) > 0);
  return [...merged, ...otherOnly];
};

// A tab still running the pre-partitioning code keeps writing to the legacy record
// after another tab has already migrated this ticket. Merging per task means a newer
// edit on either side survives; a ticket that is already fully submitted is never
// reverted to unsubmitted work, whichever side holds the submission.
const reconcileWithLegacy = (own, legacy, sowKey) => {
  const ownTasks = ticketTasksOf(own, sowKey);
  if (isFullySubmitted(ownTasks)) return own;
  const legacyTasks = ticketTasksOf(legacy, sowKey);
  // An old-code tab that submitted the ticket keeps its earlier edit timestamps, so a
  // newer unsubmitted copy would win the merge and re-enable Submit: submitted is final.
  if (isFullySubmitted(legacyTasks)) return { ...own, state: { ...own.state, jobsTasks: { [sowKey]: legacyTasks } } };
  const merged = mergeTasksById(ownTasks, legacyTasks);
  return { ...own, state: { ...own.state, jobsTasks: { [sowKey]: merged } } };
};

const recordNameFor = (sowKey) => `${TICKET_TASKS_STORAGE_PREFIX}${sowKey}`;

const tasksDiffer = (first, second, sowKey) =>
  JSON.stringify(ticketTasksOf(first, sowKey)) !== JSON.stringify(ticketTasksOf(second, sowKey));

// Copies this ticket's legacy slice into its own record so the ticket stops depending on
// the shared legacy record, which a tab on old code can still overwrite. Done in a single
// IndexedDB read-write transaction, merging with whatever another client wrote to the
// ticket record in the meantime. Returns what ended up stored.
const migrateIntoTicketRecord = async (sowKey, legacy) => {
  let stored = legacy;
  await update(recordNameFor(sowKey), (current) => {
    const own = current ? JSON.parse(current) : null;
    stored = own ? reconcileWithLegacy(own, legacy, sowKey) : legacy;
    return JSON.stringify(stored);
  });
  return stored;
};

// Right after an in-app ticket switch the previous ticket's tasks are still in memory
// while the new ticket has none yet; writing that state would clobber the new ticket's
// record with the wrong ticket's data. With no ticket in the URL there is no per-ticket
// key to check against, so everything is written.
const carriesTasksForTicket = (value, sowKey) =>
  sowKey === DEFAULT_CACHE_KEY ||
  Object.prototype.hasOwnProperty.call(value?.state?.jobsTasks ?? {}, sowKey);

// The in-memory store can hold several tickets' tasks after in-app switches; each
// record must only ever contain its own ticket's.
const scopeToTicket = (value, sowKey) =>
  sowKey === DEFAULT_CACHE_KEY
    ? value
    : { ...value, state: { ...value.state, jobsTasks: { [sowKey]: value.state.jobsTasks[sowKey] } } };

// Storage for useSOWTasksStore. The persist `name` is fixed when the store is created,
// but HomeTab switches tickets with history.pushState and no reload, so the record is
// derived from the current ticket on every call and the `name` argument is ignored.
// Each load is reconciled with this ticket's slice of the legacy record, which is never
// written or deleted: other tickets' tabs still need to migrate from it.
export const createTicketTasksStorage = (getSowKey, canWrite = () => true) => ({
  getItem: async () => {
    const sowKey = getSowKey();
    const own = await indexedDBJSONStorage.getItem(recordNameFor(sowKey));
    const legacy = await readFromLegacyRecord(sowKey);
    if (!legacy) return own;
    if (!own) return migrateIntoTicketRecord(sowKey, legacy);

    const reconciled = reconcileWithLegacy(own, legacy, sowKey);
    return tasksDiffer(own, reconciled, sowKey) ? migrateIntoTicketRecord(sowKey, legacy) : reconciled;
  },
  setItem: async (_name, value) => {
    const sowKey = getSowKey();
    // `canWrite` is false after the ticket's record could not be read: what is in memory
    // is then not its work, and saving it would overwrite the unread record.
    if (!canWrite() || !carriesTasksForTicket(value, sowKey)) return;
    try {
      await indexedDBJSONStorage.setItem(recordNameFor(sowKey), scopeToTicket(value, sowKey));
    } catch (error) {
      console.error('Failed to save tasks:', error);
    }
  },
  removeItem: () => indexedDBJSONStorage.removeItem(recordNameFor(getSowKey())),
});

// Tasks persisted for several tickets at once, for views that need progress of tickets
// other than the one this tab has open (e.g. the Home list). The legacy record is read
// once for the whole batch and nothing is written, so tickets that were never opened get
// no record of their own. Reconciliation matches the store's, so both always agree.
// Returns `{ [sowKey]: tasks }` with [] for tickets with nothing stored.
export const readStoredTicketTasksByKey = async (sowKeys) => {
  const legacy = await indexedDBJSONStorage.getItem(LEGACY_TASKS_STORAGE_NAME);
  const entries = await Promise.all(sowKeys.map(async (sowKey) => {
    const own = await indexedDBJSONStorage.getItem(recordNameFor(sowKey));
    const legacyTasks = legacy?.state?.jobsTasks?.[sowKey];
    if (!legacyTasks) return [sowKey, ticketTasksOf(own, sowKey)];
    const reconciled = own ? reconcileWithLegacy(own, legacy, sowKey) : legacy;
    return [sowKey, ticketTasksOf(reconciled, sowKey)];
  }));
  return Object.fromEntries(entries);
};

// Combines the tasks read from a ticket's record with the same ticket's tasks that were
// worked on in memory while the record could not be read: per task the newer edit wins,
// a ticket already fully submitted on either side stays submitted.
export const mergeInMemoryTasks = (storedJobsTasks, memoryJobsTasks) =>
  Object.entries(memoryJobsTasks).reduce((merged, [sowKey, memoryTasks]) => {
    const storedTasks = merged[sowKey] ?? [];
    if (isFullySubmitted(storedTasks)) return merged;
    // Submitting in memory does not bump lastModified, so older stored edits would win.
    if (isFullySubmitted(memoryTasks)) return { ...merged, [sowKey]: memoryTasks };
    // With nothing stored, merging would keep only the edited tasks and drop the rest.
    if (storedTasks.length === 0) return { ...merged, [sowKey]: memoryTasks };
    // Memory is the work done after the failed read, so it also wins an equal edit time.
    return { ...merged, [sowKey]: mergeTasksById(storedTasks, memoryTasks, { otherWinsTies: true }) };
  }, storedJobsTasks);
